import React, { ChangeEvent, useCallback, useContext } from 'react';
import noop from 'lodash/noop';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import { Flex, FlexProps } from '../layout/Flex';

type UseFormControlGroupPayload<TValue = unknown> = Partial<{
    name: string;
    type: string;
    value: TValue;
    checked: boolean;
    indeterminate: boolean;
    multiple: boolean;
    onBlur: (event?: React.FocusEvent<HTMLInputElement>) => void;
    onChange: (event: React.ChangeEvent<HTMLInputElement> | any) => void;
    onFocus: (event?: React.FocusEvent<HTMLInputElement>) => void;
}>;

const FormControlGroupContext = React.createContext<UseFormControlGroupPayload>({});

export const useFormControlGroup = (): UseFormControlGroupPayload => {
    try {
        return useContext(FormControlGroupContext);
    } catch (_error) {
        return {};
    }
};

const FORWARDED_PROPS: string[] = [
    'name',
    'type',
    'value',
    'checked',
    'multiple',
    'onBlur',
    'onChange',
    'onFocus',
];

interface FormControlGroupProps
    extends Omit<FlexProps, 'onChange' | 'onBlur' | 'onFocus'>,
        UseFormControlGroupPayload {}

export function FormControlGroup(props: FormControlGroupProps) {
    const { children, onChange: onFieldChange = noop, ...other } = props;
    const onChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            if (props.multiple) {
                const currentValue = Array.isArray(props.value) ? props.value : [props.value];

                if (event.target.checked) {
                    return onFieldChange([...currentValue, event.target.value]);
                }

                return onFieldChange(currentValue.filter((value) => value !== event.target.value));
            }

            onFieldChange(event.target.checked ? event.target.value : undefined);
        },
        [props.multiple, props.value, onFieldChange],
    );

    return (
        <FormControlGroupContext.Provider value={{ ...pick(other, FORWARDED_PROPS), onChange }}>
            <Flex {...(omit(other, FORWARDED_PROPS) as any)}>{children}</Flex>
        </FormControlGroupContext.Provider>
    );
}
